import React, { useState, useImperativeHandle, forwardRef } from 'react';

interface EarningsPopupRef {
  trigger: (amount: number) => void;
}

const EarningsPopup = forwardRef<EarningsPopupRef>((props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('hidden');
  const [currentAmount, setCurrentAmount] = useState(0);

  const playApplePaySound = () => {
    try {
      const audio = new Audio('/ApplePaySound.mp3');
      audio.volume = 0.6;
      audio.play().catch(error => {
        console.warn('Unable to play Apple Pay sound:', error);
      });
    } catch (error) {
      console.warn('Audio not supported');
    }
  };

  useImperativeHandle(ref, () => ({
    trigger: (amount: number) => {
      if (isVisible) return;
      
      setCurrentAmount(amount);
      setIsVisible(true);
      setAnimationPhase('enter');
      
      playApplePaySound();
      
      setTimeout(() => setAnimationPhase('grow'), 200);
      setTimeout(() => setAnimationPhase('float'), 600);
      setTimeout(() => setAnimationPhase('fade'), 4100);
      
      setTimeout(() => {
        setIsVisible(false);
        setAnimationPhase('hidden');
      }, 5100);
    },
  }));

  const getAnimationStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'fixed',
      top: '80px',
      right: '20px',
      zIndex: 10000,
      pointerEvents: 'none',
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#10B981',
      textShadow: '0 2px 8px rgba(0,0,0,0.3)',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '12px 20px',
      borderRadius: '12px',
      border: '2px solid #10B981',
      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
    };

    switch (animationPhase) {
      case 'enter':
        return {
          ...baseStyles,
          transform: 'translateY(-20px) scale(0.3)',
          opacity: 0,
          transition: 'all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        };
      case 'grow':
        return {
          ...baseStyles,
          transform: 'translateY(0px) scale(1.1)',
          opacity: 1,
          transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        };
      case 'float':
        return {
          ...baseStyles,
          transform: 'translateY(-80px) scale(1)',
          opacity: 1,
          transition: 'all 3.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        };
      case 'fade':
        return {
          ...baseStyles,
          transform: 'translateY(-120px) scale(0.8)',
          opacity: 0,
          transition: 'all 1.0s ease-out',
        };
      default:
        return { ...baseStyles, display: 'none' };
    }
  };

  if (!isVisible) return null;

  return (
    <div style={getAnimationStyles()}>
      <span style={{ color: '#10B981', fontSize: '28px' }}>💰</span>
      <span style={{ color: '#374151' }}>You earned ${currentAmount}!</span>
    </div>
  );
});

EarningsPopup.displayName = 'EarningsPopup';

export default EarningsPopup;